import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import BookPicker from './BookPicker'
import ProposeBooks from './ProposeBooks'
import BookVoting from './BookVoting'
import AddPastBook from './AddPastBook'
import PastBookDetail from './PastBookDetail'
import SeasonManager from './SeasonManager'
import InviteModal from './InviteModal'
import CountdownTimer from './CountdownTimer'
import ProposeMeeting from './ProposeMeeting'
import MeetingVoting from './MeetingVoting'
import BookReviews from './BookReviews'
import ClubBookSuggestions from './ClubBookSuggestions'
import UpdateProposalDeadline from './UpdateProposalDeadline'
import EarlyFinalizeProposal from './EarlyFinalizeProposal'
import UpdateReadingDeadline from './UpdateReadingDeadline'
import RecommendationChat from './RecommendationChat'
import ComposeMessage from './ComposeMessage'
import MessageHistory from './MessageHistory'
import { formatDate } from '../utils/dateFormat'
import BuyBookButton from './BuyBookButton'
import CurrentlyReadingDetail from './CurrentlyReadingDetail'
import ClubSettings from './ClubSettings'
import Footer from './Footer'
import Modal from './Modal'

interface ClubViewProps {
  clubId: number
  user: {
    id: number
    name: string
    email: string
    preferred_affiliate_provider?: string | null
  }
  onBack: () => void
  onSignOut: () => void
}

interface Member {
  id: number
  user_id: number
  name: string
  email: string
  joined_at: string
}

interface Book {
  id: number
  title: string
  author: string
  isbn?: string
  asin?: string
  bookshop_slug?: string
  description?: string
  cover_url?: string
  genre?: string
  first_publish_year?: number
  number_of_pages?: number
}

interface BookPick {
  id: number
  status: string
  started_at?: string
  completed_at?: string
  reading_deadline?: string
  created_at: string
  book: Book
  picked_by: {
    id: number
    name: string
    email: string
  } | null
  season?: {
    id: number
    season_number: number
  }
  unclaimed: boolean
  progress_stats?: {
    finished_count: number
    reading_count: number
    total_members: number
  }
  current_user_progress?: {
    status: string
    started_reading_at?: string
    finished_reading_at?: string
  }
  average_rating?: number
  review_count: number
}

interface Club {
  id: number
  name: string
  created_at: string
  invite_token: string
  preferred_affiliate_provider?: string
}

interface User {
  id: number
  name: string
  email: string
}

interface RotationSlot {
  user: User
  status: 'picked' | 'current' | 'upcoming'
  position: number
}

interface Season {
  id: number
  club_id: number
  season_number: number
  status: 'not_started' | 'active' | 'complete'
  started_at?: string
  completed_at?: string
  current_rotation_index: number
  rotation_order: RotationSlot[]
  current_picker?: User
  book_picks_count: number
}

interface BookProposalOption {
  id: number
  book: Book
  display_order: number
  borda_score?: number
}

interface BookProposal {
  id: number
  club_id: number
  proposer: {
    id: number
    name: string
    email: string
  }
  status: 'active' | 'completed' | 'cancelled'
  voting_deadline: string
  options: BookProposalOption[]
  winner_option_id?: number
  selected_option_id?: number
  user_vote?: {
    id: number
    rankings: { [key: string]: number }
  }
  vote_count: number
  voters?: { name: string }[]
  deadline_passed: boolean
  share_token?: string
  created_at: string
  updated_at: string
}

export default function ClubView({ clubId, user: initialUser, onBack, onSignOut }: ClubViewProps) {
  const navigate = useNavigate()
  const [user, setUser] = useState(initialUser)
  const [club, setClub] = useState<Club | null>(null)
  const [members, setMembers] = useState<Member[]>([])
  const [bookPicks, setBookPicks] = useState<BookPick[]>([])
  const [bookProposals, setBookProposals] = useState<BookProposal[]>([])
  const [season, setSeason] = useState<Season | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showBookPicker, setShowBookPicker] = useState(false)
  const [showProposeBooks, setShowProposeBooks] = useState(false)
  const [showBookVoting, setShowBookVoting] = useState(false)
  const [showBookPickerChoice, setShowBookPickerChoice] = useState(false)
  const [showAddPastBook, setShowAddPastBook] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showClubSettings, setShowClubSettings] = useState(false)
  const [showProposeMeeting, setShowProposeMeeting] = useState(false)
  const [showMeetingVoting, setShowMeetingVoting] = useState(false)
  const [showReviews, setShowReviews] = useState(false)
  const [selectedBookPickForReview, setSelectedBookPickForReview] = useState<number | null>(null)
  const [showPastBookDetail, setShowPastBookDetail] = useState(false)
  const [showUpdateDeadline, setShowUpdateDeadline] = useState(false)
  const [showEarlyFinalize, setShowEarlyFinalize] = useState(false)
  const [copiedShareLink, setCopiedShareLink] = useState(false)
  const [selectedProposalId, setSelectedProposalId] = useState<number | null>(null)
  const [showUpdateReadingDeadline, setShowUpdateReadingDeadline] = useState(false)
  const [selectedBookPickId, setSelectedBookPickId] = useState<number | null>(null)
  const [selectedPastBook, setSelectedPastBook] = useState<BookPick | null>(null)
  const [updatingProgress, setUpdatingProgress] = useState(false)
  const [claimingBook, setClaimingBook] = useState<number | null>(null)
  const [showRecommendationChat, setShowRecommendationChat] = useState(false)
  const [showComposeMessage, setShowComposeMessage] = useState(false)
  const [showMessageHistory, setShowMessageHistory] = useState(false)
  const [showActionsMenu, setShowActionsMenu] = useState(false)
  const [showClubProgress, setShowClubProgress] = useState(false)

  useEffect(() => {
    fetchClubData()
  }, [clubId])

  const fetchClubData = async () => {
    try {
      setLoading(true)

      // Fetch club details with members
      const clubResponse = await fetch(`/api/v1/clubs/${clubId}`)
      if (!clubResponse.ok) throw new Error('Failed to load club')
      const clubData = await clubResponse.json()
      setClub(clubData.club)
      setMembers(clubData.members || [])

      // Fetch book picks
      const picksResponse = await fetch(`/api/v1/clubs/${clubId}/book_picks`)
      if (!picksResponse.ok) throw new Error('Failed to load book picks')
      const picksData = await picksResponse.json()
      setBookPicks(picksData.book_picks || [])

      // Fetch book proposals
      const proposalsResponse = await fetch(`/api/v1/clubs/${clubId}/book_proposals`)
      if (proposalsResponse.ok) {
        const proposalsData = await proposalsResponse.json()
        setBookProposals(proposalsData.book_proposals || [])
      }

      // Fetch seasons
      const seasonsResponse = await fetch(`/api/v1/clubs/${clubId}/seasons`)
      if (seasonsResponse.ok) {
        const seasonsData = await seasonsResponse.json()
        // Use current_season if available, otherwise get the most recent season
        if (seasonsData.current_season) {
          setSeason(seasonsData.current_season)
        } else if (seasonsData.seasons && seasonsData.seasons.length > 0) {
          // Get the most recent season (last one in the ordered array)
          setSeason(seasonsData.seasons[seasonsData.seasons.length - 1])
        } else {
          setSeason(null)
        }
      }
    } catch (err) {
      setError('Failed to load club data')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleProviderSelected = async (provider: string) => {
    // Update local state immediately for responsiveness
    setUser({ ...user, preferred_affiliate_provider: provider })

    // Save to backend
    try {
      const { fetchWithCsrf } = await import('../utils/csrf')
      await fetchWithCsrf('/api/v1/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user: {
            preferred_affiliate_provider: provider
          }
        })
      })
    } catch (err) {
      console.error('Failed to save affiliate provider preference:', err)
    }
  }

  const handleMarkFinished = async (bookPickId: number) => {
    setUpdatingProgress(true)
    try {
      const { fetchWithCsrf } = await import('../utils/csrf')

      // First, mark user's progress as finished
      const progressResponse = await fetchWithCsrf(`/api/v1/book_picks/${bookPickId}/progress`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action_type: 'finish_reading' })
      })

      if (!progressResponse.ok) {
        console.error('Failed to mark as finished')
        return
      }

      // Then, move the book to completed status
      const completeResponse = await fetchWithCsrf(`/api/v1/clubs/${clubId}/book_picks/${bookPickId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          book_pick: {
            status: 'completed',
            completed_at: new Date().toISOString().split('T')[0]
          }
        })
      })

      if (completeResponse.ok) {
        // Refresh book picks to show updated status
        fetchClubData()
      } else {
        console.error('Failed to complete book')
      }
    } catch (err) {
      console.error('Error marking as finished:', err)
    } finally {
      setUpdatingProgress(false)
    }
  }

  const handleClaimBook = async (bookPickId: number) => {
    setClaimingBook(bookPickId)
    try {
      const { fetchWithCsrf } = await import('../utils/csrf')
      const response = await fetchWithCsrf(`/api/v1/clubs/${clubId}/book_picks/${bookPickId}/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })

      if (response.ok) {
        // Refresh book picks to show updated picker
        fetchClubData()
      } else {
        const data = await response.json()
        console.error('Failed to claim book:', data.error)
      }
    } catch (err) {
      console.error('Error claiming book:', err)
    } finally {
      setClaimingBook(null)
    }
  }

  const currentPick = bookPicks.find(pick => pick.status === 'current')
  const upcomingPicks = bookPicks.filter(pick => pick.status === 'upcoming')
  const completedPicks = bookPicks.filter(pick => pick.status === 'completed')

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  if (error || !club) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Club not found'}</p>
          <button
            onClick={onBack}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            {/* Top row: back button, club name, sign out */}
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4 min-w-0">
                <button
                  onClick={onBack}
                  className="text-gray-600 hover:text-gray-900 transition-colors flex-shrink-0"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                </button>
                <div className="min-w-0">
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{club.name}</h1>
                  <p className="text-sm text-gray-500">
                    {members.length} {members.length === 1 ? 'member' : 'members'}
                  </p>
                </div>
                <button
                  onClick={() => setShowClubSettings(true)}
                  className="text-gray-400 hover:text-indigo-600 transition-colors p-2 rounded-lg hover:bg-indigo-50 flex-shrink-0"
                  title="Club Settings"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
              </div>
              <button
                onClick={onSignOut}
                className="text-sm text-gray-600 hover:text-gray-900 transition-colors px-3 py-2 rounded-lg hover:bg-gray-100 flex-shrink-0"
              >
                Sign out
              </button>
            </div>
            {/* Bottom row: action buttons */}
            <div className="flex items-center gap-2 mt-3 pl-10">
              {/* Desktop: Show all buttons */}
              <div className="hidden lg:flex items-center gap-2">
                <button
                  onClick={() => navigate(`/clubs/${clubId}/stats`)}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all text-sm"
                  title="View Stats"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Stats
                </button>
                <button
                  onClick={() => setShowRecommendationChat(true)}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-white border-2 border-indigo-300 text-indigo-700 font-semibold rounded-lg hover:bg-indigo-50 hover:border-indigo-400 transition-all text-sm"
                  title="Get Recommendations"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  Get Recommendations
                </button>
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg shadow-indigo-500/30 text-sm"
                  title="Invite"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                  Invite
                </button>
                <button
                  onClick={() => setShowComposeMessage(true)}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-white border-2 border-green-300 text-green-700 font-semibold rounded-lg hover:bg-green-50 hover:border-green-400 transition-all text-sm"
                  title="Send Message"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Send Message
                </button>
                <button
                  onClick={() => setShowMessageHistory(true)}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all text-sm"
                  title="Message History"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  History
                </button>
              </div>

              {/* Mobile/Tablet: Show Invite + Menu */}
              <div className="flex lg:hidden items-center gap-2">
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg shadow-indigo-500/30 text-sm"
                  title="Invite"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                  Invite
                </button>

                {/* Dropdown Menu */}
                <div className="relative">
                  <button
                    onClick={() => setShowActionsMenu(!showActionsMenu)}
                    className="inline-flex items-center justify-center w-10 h-10 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all"
                    title="More actions"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </button>

                  {/* Dropdown Content */}
                  {showActionsMenu && (
                    <>
                      {/* Backdrop to close menu */}
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowActionsMenu(false)}
                      />
                      <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-20">
                        <button
                          onClick={() => {
                            navigate(`/clubs/${clubId}/stats`)
                            setShowActionsMenu(false)
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-3"
                        >
                          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                          <div>
                            <div className="font-medium text-gray-900">Stats</div>
                            <div className="text-xs text-gray-500">Club insights</div>
                          </div>
                        </button>
                        <button
                          onClick={() => {
                            setShowRecommendationChat(true)
                            setShowActionsMenu(false)
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-3"
                        >
                          <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                          <div>
                            <div className="font-medium text-gray-900">Get Recommendations</div>
                            <div className="text-xs text-gray-500">AI book suggestions</div>
                          </div>
                        </button>
                        <button
                          onClick={() => {
                            setShowComposeMessage(true)
                            setShowActionsMenu(false)
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-3"
                        >
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <div>
                            <div className="font-medium text-gray-900">Send Message</div>
                            <div className="text-xs text-gray-500">Email all members</div>
                          </div>
                        </button>
                        <button
                          onClick={() => {
                            setShowMessageHistory(true)
                            setShowActionsMenu(false)
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-3"
                        >
                          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <div className="font-medium text-gray-900">Message History</div>
                            <div className="text-xs text-gray-500">View past emails</div>
                          </div>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - Current & Past Picks */}
          <div className="lg:col-span-2 space-y-8">
            {/* Active Book Proposal / Voting */}
            {(() => {
              const activeProposal = bookProposals.find(p => p.status === 'active')
              if (activeProposal) {
                return (
                  <section>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Currently Reading</h2>
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                      <div className="mb-6">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-bold text-gray-900">Book Voting in Progress</h3>
                            <p className="text-sm text-gray-600 mt-1">
                              {activeProposal.proposer.name} proposed {activeProposal.options.length} books for voting
                            </p>
                          </div>
                          {activeProposal.voting_deadline && (
                            <div className="text-right">
                              <CountdownTimer deadline={activeProposal.voting_deadline} />
                            </div>
                          )}
                        </div>

                        {/* Vote count and status */}
                        <div className="flex items-center gap-4 mb-4">
                          <div className="inline-flex items-center gap-2 px-3 py-2 bg-indigo-50 rounded-lg text-sm">
                            <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                            </svg>
                            <span className="font-medium text-indigo-900">
                              {activeProposal.vote_count} of {members.length} members voted
                            </span>
                          </div>
                          {activeProposal.user_vote && (
                            <div className="inline-flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg text-sm">
                              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span className="font-medium text-green-900">You voted</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Book Options */}
                      <div className="space-y-3">
                        {activeProposal.options.map((option) => (
                          <div key={option.id} className="flex gap-4 p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
                            {option.book.cover_url ? (
                              <img
                                src={option.book.cover_url}
                                alt={option.book.title}
                                className="w-16 h-24 object-cover rounded shadow-sm flex-shrink-0"
                              />
                            ) : (
                              <div className="w-16 h-24 bg-gradient-to-br from-indigo-100 to-purple-100 rounded flex items-center justify-center flex-shrink-0">
                                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                </svg>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-gray-900">{option.book.title}</h4>
                              <p className="text-sm text-gray-600">by {option.book.author}</p>
                              {option.book.description && (
                                <p className="text-sm text-gray-500 mt-1 line-clamp-2">{option.book.description}</p>
                              )}
                            </div>
                            {activeProposal.user_vote?.rankings[option.id.toString()] && (
                              <div className="flex items-center">
                                <div className="w-8 h-8 rounded-full bg-indigo-600 text-white font-bold flex items-center justify-center">
                                  #{activeProposal.user_vote.rankings[option.id.toString()]}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Voting button */}
                      <div className="mt-6">
                        <button
                          onClick={() => setShowBookVoting(true)}
                          className="w-full px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg shadow-indigo-500/30"
                        >
                          {activeProposal.user_vote ? 'Update Your Vote' : 'Cast Your Vote'}
                        </button>
                      </div>

                      {/* Proposer-only actions */}
                      {activeProposal.proposer.id === user.id && (
                        <div className="mt-6 pt-6 border-t border-gray-200">
                          {/* Who has voted */}
                          {activeProposal.voters && activeProposal.voters.length > 0 && (
                            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Voted</p>
                              <div className="flex flex-wrap gap-2">
                                {activeProposal.voters.map((voter, i) => (
                                  <span key={i} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    {voter.name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Live standings */}
                          {activeProposal.options.some(o => o.borda_score != null) && (
                            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Current Standings</p>
                              <div className="space-y-2">
                                {[...activeProposal.options]
                                  .sort((a, b) => (b.borda_score || 0) - (a.borda_score || 0))
                                  .map(option => (
                                    <div key={option.id} className="flex items-center justify-between text-sm">
                                      <span className="text-gray-700 font-medium truncate mr-2">{option.book.title}</span>
                                      <span className="text-indigo-600 font-semibold flex-shrink-0">{option.borda_score} pts</span>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )}

                          <p className="text-sm font-semibold text-gray-700 mb-3">Proposer Actions</p>
                          <div className="flex flex-col sm:flex-row gap-3">
                            <button
                              onClick={() => {
                                setSelectedProposalId(activeProposal.id)
                                setShowUpdateDeadline(true)
                              }}
                              className="flex-1 px-4 py-2 bg-white text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-all border-2 border-gray-300 flex items-center justify-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              Update Deadline
                            </button>
                            <button
                              onClick={() => {
                                setSelectedProposalId(activeProposal.id)
                                setShowEarlyFinalize(true)
                              }}
                              className="flex-1 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-medium rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all shadow-md flex items-center justify-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Select Winner Now
                            </button>
                          </div>
                          {activeProposal.share_token && (
                            <button
                              onClick={() => {
                                const url = `${window.location.origin}/vote/${activeProposal.share_token}`
                                navigator.clipboard.writeText(url).then(() => {
                                  setCopiedShareLink(true)
                                  setTimeout(() => setCopiedShareLink(false), 2000)
                                })
                              }}
                              className="mt-3 w-full px-4 py-2 bg-white text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-all border-2 border-gray-300 flex items-center justify-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                              </svg>
                              {copiedShareLink ? 'Link Copied!' : 'Copy Voting Link'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </section>
                )
              }
              return null
            })()}

            {/* Current Pick - Only show if no active proposal */}
            {!bookProposals.find(p => p.status === 'active') && currentPick && (
              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Currently Reading</h2>
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                  <div className="flex flex-col sm:flex-row gap-6">
                    {currentPick.book.cover_url ? (
                      <img
                        src={currentPick.book.cover_url}
                        alt={currentPick.book.title}
                        className="w-32 h-48 object-cover rounded-lg shadow-md"
                      />
                    ) : (
                      <div className="w-32 h-48 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg shadow-md flex items-center justify-center">
                        <svg className="w-12 h-12 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{currentPick.book.title}</h3>
                      <p className="text-lg text-gray-600 mb-4">by {currentPick.book.author}</p>
                      {currentPick.book.description && (
                        <p className="text-gray-700 mb-4 line-clamp-3">{currentPick.book.description}</p>
                      )}

                      {/* Buy Book Button */}
                      <div className="mb-4">
                        <BuyBookButton
                          book={currentPick.book}
                          userProvider={user.preferred_affiliate_provider}
                          variant="primary"
                          size="md"
                        />
                      </div>

                      <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                        {currentPick.picked_by ? (
                          <span>Picked by {currentPick.picked_by.name}</span>
                        ) : (
                          <span className="text-gray-400 italic">(Unclaimed)</span>
                        )}
                        {currentPick.started_at && (
                          <>
                            <span>•</span>
                            <span>Started {formatDate(currentPick.started_at)}</span>
                          </>
                        )}
                      </div>

                      {/* Reading Deadline Countdown */}
                      {currentPick.reading_deadline && (
                        <div className="mb-4">
                          <CountdownTimer deadline={currentPick.reading_deadline} />
                        </div>
                      )}

                      {/* Progress Stats */}
                      {currentPick.progress_stats && (
                        <div className="mb-4 inline-flex items-center gap-2 px-3 py-2 bg-indigo-50 rounded-lg text-sm">
                          <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="font-medium text-indigo-900">
                            {currentPick.progress_stats.finished_count} of {currentPick.progress_stats.total_members} members finished
                          </span>
                        </div>
                      )}

                      {/* User Progress Actions */}
                      <div className="space-y-3">
                        {currentPick.current_user_progress?.status === 'finished' ? (
                          <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
                            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-sm font-medium text-green-900">
                              You finished this book on {formatDate(currentPick.current_user_progress.finished_reading_at!)}
                            </span>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleMarkFinished(currentPick.id)}
                            disabled={updatingProgress}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/30"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {updatingProgress ? 'Finishing...' : 'Mark as Finished'}
                          </button>
                        )}

                        {/* Meeting Actions */}
                        <div className="flex flex-wrap gap-2">
                          {currentPick.picked_by?.id === user.id ? (
                            <button
                              onClick={() => setShowProposeMeeting(true)}
                              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white border-2 border-indigo-300 text-indigo-700 font-semibold rounded-lg hover:bg-indigo-50 transition-all"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              Propose Meeting Dates
                            </button>
                          ) : null}
                          <button
                            onClick={() => setShowMeetingVoting(true)}
                            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                            </svg>
                            Vote on Dates
                          </button>
                          <button
                            onClick={() => setShowClubProgress(true)}
                            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white border-2 border-blue-300 text-blue-700 font-semibold rounded-lg hover:bg-blue-50 transition-all"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                            See Club Progress
                          </button>
                          {currentPick.reading_deadline && (
                            <button
                              onClick={() => {
                                setSelectedBookPickId(currentPick.id)
                                setShowUpdateReadingDeadline(true)
                              }}
                              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-white border-2 border-amber-300 text-amber-700 font-semibold rounded-lg hover:bg-amber-50 transition-all"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Update Deadline
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* No Current Pick - Only show if no active proposal and no current pick */}
            {!bookProposals.find(p => p.status === 'active') && !currentPick && (
              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Currently Reading</h2>
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-12 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full mb-4">
                    <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No current pick</h3>

                  {/* Show different messaging based on season state */}
                  {season?.status === 'active' && season.current_picker ? (
                    season.current_picker.id === user.id ? (
                      <>
                        <p className="text-gray-600 mb-6">It's your turn to pick a book!</p>
                        <button
                          onClick={() => setShowBookPickerChoice(true)}
                          className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg shadow-indigo-500/30"
                        >
                          Pick a Book
                        </button>
                      </>
                    ) : (
                      <>
                        <p className="text-gray-600 mb-2">Waiting for {season.current_picker.name} to pick</p>
                        <p className="text-sm text-gray-500">It's their turn in the rotation</p>
                      </>
                    )
                  ) : (
                    <>
                      <p className="text-gray-600 mb-6">Pick a book to start reading together!</p>
                      <button
                        onClick={() => setShowBookPickerChoice(true)}
                        className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg shadow-indigo-500/30"
                      >
                        Pick a Book
                      </button>
                    </>
                  )}
                </div>
              </section>
            )}

            {/* Upcoming Picks */}
            {upcomingPicks.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Up Next</h2>
                <div className="space-y-4">
                  {upcomingPicks.map(pick => (
                    <div key={pick.id} className="bg-white rounded-xl shadow-md border border-gray-200 p-4 hover:shadow-lg transition-shadow">
                      <div className="flex flex-col sm:flex-row gap-4">
                        {pick.book.cover_url ? (
                          <img
                            src={pick.book.cover_url}
                            alt={pick.book.title}
                            className="w-16 h-24 object-cover rounded"
                          />
                        ) : (
                          <div className="w-16 h-24 bg-gradient-to-br from-indigo-100 to-purple-100 rounded flex items-center justify-center">
                            <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{pick.book.title}</h3>
                          <p className="text-sm text-gray-600">by {pick.book.author}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {pick.picked_by ? `Picked by ${pick.picked_by.name}` : <span className="italic">(Unclaimed)</span>}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Past Picks - Always show section to allow adding past books */}
            <section>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">Past Reads</h2>
                <div className="flex gap-2">
                  {completedPicks.length > 0 && (
                    <button
                      onClick={() => navigate(`/clubs/${clubId}/past-reads`)}
                      className="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors"
                    >
                      See All
                    </button>
                  )}
                  <button
                    onClick={() => setShowAddPastBook(true)}
                    className="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors"
                  >
                    + Add Past Book
                  </button>
                </div>
              </div>
                {completedPicks.length > 0 ? (
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-200 divide-y divide-gray-200">
                    {completedPicks.slice(0, 10).map(pick => (
                      <div
                        key={pick.id}
                        className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => {
                          setSelectedPastBook(pick)
                          setShowPastBookDetail(true)
                        }}
                      >
                        <div className="flex flex-col sm:flex-row gap-4">
                          {pick.book.cover_url ? (
                            <img
                              src={pick.book.cover_url}
                              alt={pick.book.title}
                              className="w-12 h-18 object-cover rounded"
                            />
                          ) : (
                            <div className="w-12 h-18 bg-gradient-to-br from-gray-100 to-gray-200 rounded flex items-center justify-center">
                              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                              </svg>
                            </div>
                          )}
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900">{pick.book.title}</h3>
                            <p className="text-sm text-gray-600">by {pick.book.author}</p>
                            <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                              {pick.picked_by ? (
                                <span>Picked by {pick.picked_by.name}</span>
                              ) : (
                                <span className="text-gray-400 italic">(Unclaimed)</span>
                              )}
                              {pick.completed_at && (
                                <>
                                  <span>•</span>
                                  <span>Completed {formatDate(pick.completed_at)}</span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setSelectedBookPickForReview(pick.id)
                                setShowReviews(true)
                              }}
                              className="px-3 py-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors"
                            >
                              Reviews
                            </button>
                            {pick.unclaimed && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleClaimBook(pick.id)
                                }}
                                disabled={claimingBook === pick.id}
                                className="px-3 py-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {claimingBook === pick.id ? 'Claiming...' : 'Claim'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full mb-3">
                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    </div>
                    <p className="text-gray-600 mb-4">No past books yet</p>
                    <button
                      onClick={() => setShowAddPastBook(true)}
                      className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg shadow-indigo-500/30"
                    >
                      Add Your First Past Book
                    </button>
                  </div>
                )}
            </section>

            {/* Book Suggestions */}
            <section>
              <ClubBookSuggestions
                clubId={clubId}
                currentUser={user}
                preferredProvider={user.preferred_affiliate_provider}
                onProviderSelected={handleProviderSelected}
              />
            </section>
          </div>

          {/* Sidebar - Season & Members */}
          <div className="lg:col-span-1 space-y-6">
            {/* Season Manager */}
            <SeasonManager
              clubId={clubId}
              season={season}
              currentUser={user}
              onSeasonChange={fetchClubData}
            />

            {/* Members */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Members</h2>
              <div className="space-y-3">
                {members.map(member => (
                  <div key={member.id} className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-semibold text-indigo-600">
                        {member.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{member.name}</p>
                      <p className="text-xs text-gray-500 truncate">{member.email}</p>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={() => setShowInviteModal(true)}
                className="w-full mt-6 py-2.5 px-4 border-2 border-dashed border-gray-300 hover:border-indigo-400 text-gray-600 hover:text-indigo-600 font-medium rounded-lg transition-colors text-sm"
              >
                + Invite Members
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Book Picker Choice Modal */}
      <Modal
        isOpen={showBookPickerChoice}
        onClose={() => setShowBookPickerChoice(false)}
        size="md"
        showCloseButton={false}
      >
        <div className="p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose How to Pick</h2>
          <p className="text-gray-600 mb-6">Select a book directly or propose options for voting</p>

          <div className="space-y-3">
            <button
              onClick={() => {
                setShowBookPickerChoice(false)
                setShowBookPicker(true)
              }}
              className="w-full flex items-start gap-4 p-4 bg-white border-2 border-indigo-300 hover:border-indigo-500 rounded-xl transition-all group"
            >
              <div className="w-10 h-10 bg-indigo-100 group-hover:bg-indigo-200 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="text-left flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">Pick a Book</h3>
                <p className="text-sm text-gray-600">Choose a book directly and start reading</p>
              </div>
            </button>

            <button
              onClick={() => {
                setShowBookPickerChoice(false)
                setShowProposeBooks(true)
              }}
              className="w-full flex items-start gap-4 p-4 bg-white border-2 border-purple-300 hover:border-purple-500 rounded-xl transition-all group"
            >
              <div className="w-10 h-10 bg-purple-100 group-hover:bg-purple-200 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div className="text-left flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">Propose Books to Vote</h3>
                <p className="text-sm text-gray-600">Let members vote on 2-5 book options</p>
              </div>
            </button>
          </div>

          <button
            onClick={() => setShowBookPickerChoice(false)}
            className="w-full mt-4 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-all"
          >
            Cancel
          </button>
        </div>
      </Modal>

      {/* Book Picker Modal */}
      {showBookPicker && (
        <BookPicker
          clubId={clubId}
          onClose={() => setShowBookPicker(false)}
          onSuccess={() => {
            setShowBookPicker(false)
            fetchClubData()
          }}
          userProvider={user.preferred_affiliate_provider}
        />
      )}

      {/* Propose Books Modal */}
      {showProposeBooks && (
        <ProposeBooks
          clubId={clubId}
          onClose={() => setShowProposeBooks(false)}
          onSuccess={() => {
            setShowProposeBooks(false)
            fetchClubData()
          }}
        />
      )}

      {/* Book Voting Modal */}
      {showBookVoting && bookProposals.find(p => p.status === 'active') && (
        <BookVoting
          proposal={bookProposals.find(p => p.status === 'active')!}
          onClose={() => setShowBookVoting(false)}
          onSuccess={() => {
            setShowBookVoting(false)
            fetchClubData()
          }}
        />
      )}

      {/* Update Deadline Modal */}
      {showUpdateDeadline && selectedProposalId && (() => {
        const proposal = bookProposals.find(p => p.id === selectedProposalId)
        return proposal ? (
          <UpdateProposalDeadline
            proposalId={proposal.id}
            currentDeadline={proposal.voting_deadline}
            onClose={() => {
              setShowUpdateDeadline(false)
              setSelectedProposalId(null)
            }}
            onSuccess={() => {
              setShowUpdateDeadline(false)
              setSelectedProposalId(null)
              fetchClubData()
            }}
          />
        ) : null
      })()}

      {/* Early Finalize Modal */}
      {showEarlyFinalize && selectedProposalId && (() => {
        const proposal = bookProposals.find(p => p.id === selectedProposalId)
        return proposal ? (
          <EarlyFinalizeProposal
            proposalId={proposal.id}
            options={proposal.options}
            deadlinePassed={proposal.deadline_passed}
            onClose={() => {
              setShowEarlyFinalize(false)
              setSelectedProposalId(null)
            }}
            onSuccess={() => {
              setShowEarlyFinalize(false)
              setSelectedProposalId(null)
              fetchClubData()
            }}
          />
        ) : null
      })()}

      {/* Update Reading Deadline Modal */}
      {showUpdateReadingDeadline && selectedBookPickId && currentPick && (
        <UpdateReadingDeadline
          clubId={clubId}
          bookPickId={selectedBookPickId}
          currentDeadline={currentPick.reading_deadline!}
          onClose={() => {
            setShowUpdateReadingDeadline(false)
            setSelectedBookPickId(null)
          }}
          onSuccess={() => {
            setShowUpdateReadingDeadline(false)
            setSelectedBookPickId(null)
            fetchClubData()
          }}
        />
      )}

      {/* Add Past Book Modal */}
      {showAddPastBook && (
        <AddPastBook
          clubId={clubId}
          onClose={() => setShowAddPastBook(false)}
          onSuccess={() => {
            setShowAddPastBook(false)
            fetchClubData()
          }}
        />
      )}

      {/* Invite Modal */}
      {showInviteModal && club && (
        <InviteModal
          clubId={clubId}
          clubName={club.name}
          inviteToken={club.invite_token}
          onClose={() => setShowInviteModal(false)}
          onTokenRegenerated={(newToken) => {
            setClub({ ...club, invite_token: newToken })
          }}
        />
      )}

      {/* Club Settings Modal */}
      {showClubSettings && club && (
        <ClubSettings
          clubId={clubId}
          clubName={club.name}
          preferredProvider={club.preferred_affiliate_provider}
          onClose={() => setShowClubSettings(false)}
          onUpdate={(name, provider) => {
            setClub({ ...club, name, preferred_affiliate_provider: provider })
            setShowClubSettings(false)
          }}
        />
      )}

      {/* Propose Meeting Modal */}
      {showProposeMeeting && currentPick && (
        <ProposeMeeting
          bookPickId={currentPick.id}
          onClose={() => setShowProposeMeeting(false)}
          onSuccess={() => {
            setShowProposeMeeting(false)
            fetchClubData()
          }}
        />
      )}

      {/* Meeting Voting Modal */}
      {showMeetingVoting && currentPick && (
        <MeetingVoting
          bookPickId={currentPick.id}
          currentUserId={user.id}
          isBookPicker={currentPick.picked_by?.id === user.id}
          onClose={() => setShowMeetingVoting(false)}
          onSuccess={() => {
            setShowMeetingVoting(false)
            fetchClubData()
          }}
        />
      )}

      {/* Book Reviews Modal */}
      {showReviews && selectedBookPickForReview && (
        <BookReviews
          bookPickId={selectedBookPickForReview}
          currentUserId={user.id}
          onClose={() => {
            setShowReviews(false)
            setSelectedBookPickForReview(null)
          }}
        />
      )}

      {/* Past Book Detail Modal */}
      {showPastBookDetail && selectedPastBook && (
        <PastBookDetail
          bookPick={selectedPastBook}
          clubId={clubId}
          onClose={() => {
            setShowPastBookDetail(false)
            setSelectedPastBook(null)
          }}
          onDelete={() => {
            fetchClubData()
          }}
          userProvider={user.preferred_affiliate_provider}
          onProviderSelected={handleProviderSelected}
        />
      )}

      {/* Recommendation Chat Modal */}
      {showRecommendationChat && club && (
        <RecommendationChat
          mode="club"
          clubId={clubId}
          clubName={club.name}
          onClose={() => {
            setShowRecommendationChat(false)
            fetchClubData() // Refresh when modal closes
          }}
          onBookChosen={() => {
            // Don't refresh here - it causes remount and loses button state
            // Data will refresh when modal closes
          }}
        />
      )}

      {/* Compose Message Modal */}
      {showComposeMessage && (
        <ComposeMessage
          clubId={clubId}
          memberCount={members.length}
          onClose={() => setShowComposeMessage(false)}
          onSuccess={() => {
            setShowComposeMessage(false)
            // Could show a success toast here
          }}
        />
      )}

      {/* Message History Modal */}
      {showMessageHistory && (
        <MessageHistory
          clubId={clubId}
          onClose={() => setShowMessageHistory(false)}
        />
      )}

      {/* Club Progress Modal */}
      {showClubProgress && currentPick && (
        <CurrentlyReadingDetail
          reading={{
            id: currentPick.id,
            club_id: currentPick.club_id,
            club_name: club?.name || '',
            status: currentPick.status,
            started_at: currentPick.started_at,
            book: currentPick.book,
            picked_by: currentPick.picked_by,
            user_progress: currentPick.current_user_progress,
            club_member_progress: currentPick.club_member_progress
          }}
          onClose={() => setShowClubProgress(false)}
          onComplete={() => {
            setShowClubProgress(false)
            fetchClubData()
          }}
          onDelete={() => {
            fetchClubData()
          }}
          userProvider={user.preferred_affiliate_provider}
          onProviderSelected={handleProviderSelected}
          showClubProgress={true}
        />
      )}

      {/* Footer */}
      <Footer />
    </div>
  )
}
